import { useQueries, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../../reducers";
import { useRouteLayer } from "../../../../../../hooks/useCurrentEntities";
import { prepareChecksForPDF, ReportTemplate } from ".";
import { CHECK_LOCATION } from "../../../../../../actions/Layers/constants";
import { format2Frontend } from "../../../../../../actions/Layers/util";
import useConfig from "../../../../../../actions/Tenants/config/configHook";
import { calculateMinMaxAvg } from "../../../../../../actions/Tenants/config/utils";
import { getFilledArrayOrDefault } from "../../../../../../utils";
import { callAvosApi } from "../../../../../../utils/useAvosApiHook";
import { TenantDefect, useDefectsUtil, useTenantDefectDefinitions } from "../../../../../Forms/useManagedOptionsHook";
import { MergeStrategy } from "../../../../Tenants/Forms/PDFDynamicSectionsConfigForm";
import { calculateStatusPercentages } from "../PDFPalletScoringOverview";
import { setPDFDataForIntakeReport } from "./intake";
import {
    buildSectionCheckLocation,
    childrenWithLayers,
    deriveFruitTypesFromLayers,
    getReportHierarchyRequestParams,
    groupChecksByDay,
    pickLatestPerLayer,
    pickLatestPreferredCheck,
    selectParentPerFruitTypeChecks,
    selectPreferredOrFallbackChecks,
} from "./pdfReportDataHelpers";

export const useDynamicSectionsData = () => {
    const config = useConfig();
    const params = useParams();

    const user = useSelector<RootState>((state) => state.auth.user);
    const tenantsIsLoading = useSelector<RootState>((state) => state.tenants.isLoading);
    const layer = useRouteLayer();

    const { getDefectsForFruitType } = useDefectsUtil();

    const reportConfig = config.get_pdf_report(layer, params?.config_value || "") as any;
    const { childrenLevel, returnGrandChildren } = getReportHierarchyRequestParams(
        reportConfig?.children_level
    );

    const checkTypes = reportConfig?.dynamic_sections?.reduce((acc, c) => {
        if (c.check_type && !acc.includes(c.check_type)) {
            acc.push(c.check_type);
        }

        return acc;
    }, [])?.join(",") || reportConfig?.check_types?.join(",") || (reportConfig?.template === ReportTemplate.advance_ripening ? CHECK_LOCATION.ADVANCE_RIPENING : params?.config_value);

    const allDefects = useTenantDefectDefinitions(layer.fruit_type?.split(",")) as TenantDefect[];

    const enableQueries = !!layer.id
        && !!reportConfig?.value
        && reportConfig?.template === ReportTemplate.dynamic_sections
        && layer?.id === params.layer_id
        && !!checkTypes;


    const fetchSectionsData = async (layer, checkTypes, fallbackTypes) => {
        try {
            const check_location = buildSectionCheckLocation(
                getFilledArrayOrDefault(checkTypes),
                getFilledArrayOrDefault(fallbackTypes),
            );

            const { data: checks } = await callAvosApi(`/layers/${layer.id}/checks`, {
                params: {
                    with_fruit: true,
                    with_meta: true,
                    check_location,
                    view_strategy: "children",
                    children_level: childrenLevel,
                }
            });

            const formattedChecks = await prepareChecksForPDF(checks, config?.root_config?.logo_path);

            return formattedChecks;
        } catch (error) {
            console.error("Error fetching checks for PDF report:", error); // eslint-disable-line no-console
            throw error;
        }
    };

    const dynamicSectionsQueries = useQueries({
        queries: (reportConfig?.dynamic_sections ?? []).map((section) => {
            return {
                queryKey: ["section", section, layer, childrenLevel],
                queryFn: () => fetchSectionsData(layer, section.check_types, section.fallback_check_types),
                enabled: enableQueries,
                refetchOnWindowFocus: false,
            };
        }),
    });

    const fetchChildrenData = async (layer) => {
        const { data: children } = await callAvosApi(`/layers/${layer.id}/children`, {
            params: {
                return_grand_children_for_report: returnGrandChildren,
                with_meta: true,
            }
        });

        // Deduplicate flattened children by ID to prevent duplicates from hierarchical structure
        const deduplicatedChildren = Array.from(new Map((children?.results || []).map((child) => [child.id, child])).values());
        // Children without a fruit type are grouped under "unknown" so they are never silently dropped by fruit-type grouping downstream.
        const formattedChildren = deduplicatedChildren.map((child) => {
            const formatted = format2Frontend(child);
            const fruit_type = typeof formatted.fruit_type === "string" ? formatted.fruit_type.trim() : formatted.fruit_type;
            return { ...formatted, fruit_type: fruit_type || "unknown" };
        });

        return { count: formattedChildren.length, results: formattedChildren };
    };

    const { data: childrenData, isLoading: isLoadingChildren } = useQuery({
        queryKey: ["children", layer, childrenLevel],
        queryFn: () => fetchChildrenData(layer),
        enabled: enableQueries,
        refetchOnWindowFocus: false,
    });

    const isLoadingChecksData = dynamicSectionsQueries.some((q) => q.isLoading);
    const dynamicSectionsData: any[] = dynamicSectionsQueries.map(({ data }, idx) => {
        const { checks, logo_base64 } = data || {} as any;
        const sectionConfig = reportConfig?.dynamic_sections?.[idx];
        const preferredCheckLocations = getFilledArrayOrDefault(sectionConfig?.check_types);
        const fallbackCheckTypes = getFilledArrayOrDefault(sectionConfig?.fallback_check_types);

        const showDryMatterMinMax = !!sectionConfig.additional_details?.includes("dry_matter_min_max");
        const showImages = !!sectionConfig.additional_details?.includes("images");
        const showDefects = !!sectionConfig.additional_details?.includes("defects");
        const dryMatterValues = checks
            ?.filter((c) => c.location === "lab_check").data
            ?.flatMap((item) => item.avocados)
            ?.filter((i) => i.manual_dry_matter)
            ?.map((i) => i.manual_dry_matter);
        const dryMatterMinMaxAvg = dryMatterValues?.length ? calculateMinMaxAvg(dryMatterValues, "") : null;

        const showScoringOverview = !!sectionConfig.additional_details?.includes("scoring_overview");
        const overruled_status = layer?.[reportConfig.manual_flag_field];

        const mergeStrategy = sectionConfig?.merge_strategy || MergeStrategy.merge_all_latest;

        // Filter children to only those that have the section's checks. The parent layer itself
        // is included as a candidate so that a check done on the parent (e.g. a container-level
        // check) gets its own render unit / starting page, not just checks on (grand)children.
        // Because checks are fetched per section by check type, the parent is only pulled in for
        // sections whose check actually lives on the parent.
        const layerIds = Array.from(new Set((checks?.map((c) => c.layer_id) || []) as string[]));
        const layerCandidates = [layer, ...getFilledArrayOrDefault(childrenData?.results)];
        const relevantLayers = childrenWithLayers(layerCandidates, layerIds);
        const fruitTypes = deriveFruitTypesFromLayers(layerCandidates);

        const showGeneralStatus = !!sectionConfig.additional_details?.includes("general_status");
        const businessRulesStatus = layer?.[reportConfig?.business_rule_flag_field as string];
        const generalStatus = layer?.[reportConfig?.manual_flag_field as string]?.replace("flag-", "") || businessRulesStatus;
        const statusOfLayer = (l) => l?.[reportConfig?.manual_flag_field as string]?.replace("flag-", "") || l?.[reportConfig?.business_rule_flag_field as string];

        // Build render units per merge strategy. Each unit is one page driven by a (possibly merged) check
        // on a specific layer (or parent), with scoped children context.
        const makeRenderUnit = (check: any, unitChildren: any[], extra = {}) => ({
            key: `${check.layer_id}-${check.check_id}${(extra as any).dayKey ? `-${(extra as any).dayKey}` : ""}`,
            layer: check.layer,
            latestCheck: check,
            checks: [check],
            children: unitChildren,
            fruit_type: check.layer?.fruit_type,
            generalStatus: showGeneralStatus ? statusOfLayer(check.layer) : undefined,
            ...extra,
        });

        let renderUnits: any[] = [];
        switch (mergeStrategy) {
        case MergeStrategy.latest_only: {
            const selected = selectPreferredOrFallbackChecks(checks, preferredCheckLocations, fallbackCheckTypes);
            const latestChecks = pickLatestPerLayer(selected);
            const latestData = setPDFDataForIntakeReport(
                config,
                user,
                allDefects,
                { results: relevantLayers ?? [] },
                latestChecks,
                {
                    preferred_check_locations: preferredCheckLocations,
                    fallback_check_locations: fallbackCheckTypes,
                    returnOnlyDirectChildrenChecks: true,
                    fallback_check_max_age_days: reportConfig?.fallback_check_max_age_days?.find((i) => i.fruit_type === layer.fruit_type)?.max_age_days,
                    tableData: sectionConfig.table_data,
                }
            );
            renderUnits = latestData.checks.map((c) => makeRenderUnit(c, latestData.children));
            break;
        }
        case MergeStrategy.merge_per_day: {
            const selected = selectPreferredOrFallbackChecks(checks, preferredCheckLocations, fallbackCheckTypes);
            renderUnits = groupChecksByDay(selected).flatMap(([dayKey, dayChecks]) => {
                const dayData = setPDFDataForIntakeReport(
                    config,
                    user,
                    allDefects,
                    { results: relevantLayers ?? [] },
                    dayChecks,
                    {
                        preferred_check_locations: preferredCheckLocations,
                        fallback_check_locations: fallbackCheckTypes,
                        returnOnlyDirectChildrenChecks: true,
                        fallback_check_max_age_days: reportConfig?.fallback_check_max_age_days?.find((i) => i.fruit_type === layer.fruit_type)?.max_age_days,
                        tableData: sectionConfig.table_data,
                    }
                );
                return dayData.checks.map((c) => makeRenderUnit(c, dayData.children, { dayKey }));
            });
            break;
        }
        case MergeStrategy.one_per_check: {
            const selected = selectPreferredOrFallbackChecks(checks, preferredCheckLocations, fallbackCheckTypes);
            renderUnits = selected.flatMap((check) => {
                const singleCheckData = setPDFDataForIntakeReport(
                    config,
                    user,
                    allDefects,
                    { results: relevantLayers ?? [] },
                    [check],
                    {
                        preferred_check_locations: preferredCheckLocations,
                        fallback_check_locations: fallbackCheckTypes,
                        returnOnlyDirectChildrenChecks: true,
                        fallback_check_max_age_days: reportConfig?.fallback_check_max_age_days?.find((i) => i.fruit_type === layer.fruit_type)?.max_age_days,
                        tableData: sectionConfig.table_data,
                    }
                );
                return singleCheckData.checks.map((c) => makeRenderUnit(c, singleCheckData.children));
            });
            break;
        }
        case MergeStrategy.parent_per_fruit_type: {
            // `selected` already applies this section's preferred and fallback check types.
            const selected = selectPreferredOrFallbackChecks(checks, preferredCheckLocations, fallbackCheckTypes);
            renderUnits = fruitTypes.map((fruit_type) => {
                const fruitTypeLayers = relevantLayers.filter((c) => c.fruit_type === fruit_type);
                const { fruitTypeChecks, parentChecks, unitChecks } = selectParentPerFruitTypeChecks(selected, fruitTypeLayers, layer.id);
                const latestCheck = pickLatestPreferredCheck(parentChecks, layer.id, preferredCheckLocations)
                    || parentChecks[0]
                    || fruitTypeChecks[0];
                const ftData = setPDFDataForIntakeReport(
                    config,
                    user,
                    allDefects,
                    { results: fruitTypeLayers },
                    unitChecks,
                    {
                        preferred_check_locations: preferredCheckLocations,
                        fallback_check_locations: fallbackCheckTypes,
                        returnOnlyDirectChildrenChecks: true,
                        fallback_check_max_age_days: reportConfig?.fallback_check_max_age_days?.find((i) => i.fruit_type === fruit_type)?.max_age_days,
                        tableData: sectionConfig.table_data,
                    }
                );
                const checksForUnit = getFilledArrayOrDefault(ftData.checks);
                const checks = checksForUnit.length > 0 ? checksForUnit : [];
                if (checks.length === 0 && latestCheck) {
                    checks.push(latestCheck);
                }
                return {
                    key: `parent-${fruit_type}`,
                    layer: { ...layer, fruit_type },
                    latestCheck,
                    checks,
                    children: fruitTypeLayers,
                    fruit_type,
                    generalStatus: showGeneralStatus ? generalStatus : undefined,
                    scoringOverview: showScoringOverview
                        ? { overruled_status, percentages: calculateStatusPercentages(fruitTypeLayers, preferredCheckLocations) }
                        : undefined,
                    dryMatterMinMax: showDryMatterMinMax ? dryMatterMinMaxAvg : undefined,
                };
            });
            break;
        }
        case MergeStrategy.merge_all_latest:
        default: {
            const allData = setPDFDataForIntakeReport(
                config,
                user,
                allDefects,
                { results: relevantLayers ?? [] },
                checks || [],
                {
                    preferred_check_locations: preferredCheckLocations,
                    fallback_check_locations: fallbackCheckTypes,
                    returnOnlyDirectChildrenChecks: true,
                    fallback_check_max_age_days: reportConfig?.fallback_check_max_age_days?.find((i) => i.fruit_type === layer.fruit_type)?.max_age_days,
                    tableData: sectionConfig.table_data,
                }
            );
            renderUnits = allData.checks.map((c) => {
                const unitLayers = childrenWithLayers(relevantLayers, [c.layer_id]);
                return makeRenderUnit(c, unitLayers);
            });
            break;
        }
        }

        // For rendering, also compute section-level aggregates (latest check for parent, dry matter, percentages)
        const latestCheck = pickLatestPreferredCheck(checks, layer.id, preferredCheckLocations);
        const sectionLayers = relevantLayers;
        const percentages = calculateStatusPercentages(sectionLayers, preferredCheckLocations);

        return {
            logo_base64,
            children: sectionLayers,
            checks: checks || [],
            renderUnits,
            mergeStrategy,
            latestCheck,
            dryMatterMinMax: showDryMatterMinMax ? dryMatterMinMaxAvg : undefined,
            scoringOverview: showScoringOverview ? { overruled_status, percentages } : undefined,
            generalStatus: showGeneralStatus ? generalStatus : undefined,
            showImages,
            showDefects,
            fruitTypes,
            defectDefinitions: getDefectsForFruitType(fruitTypes)
        };
    });

    return {
        dynamicSectionsData,
        isLoading: isLoadingChildren || isLoadingChecksData || tenantsIsLoading,
    };
};
